import { ReactElement, useEffect, useState } from 'react';
import { PageWithSidebar } from '../PageTypes';
import { useConcreteProject } from '../../ContextProviders/ProjectContext';
import { SettingsSidebar } from './SettingsSidebar';
import { useFirestore, useStorage } from '../../ContextProviders/Firebase';
import { IconButton } from '../../Buttons/Buttons';
import { faSave, faDownload, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { localizedStrings } from '../../../localizedStrings';
import { ref, getDownloadURL, getMetadata, FullMetadata, updateMetadata } from '@firebase/storage';
import { invocationActions } from '../../../Hooks/DatabaseActions';
import { useProjectConfig } from '../../ContextProviders/AppContext';
import { toasts } from '../../../shared';
import { Row } from 'reactstrap';

export const Export = (): ReactElement => {
  const project = useConcreteProject();
  const firestore = useFirestore();
  const projectConfig = useProjectConfig();
  const storage = useStorage();
  const fileRef = ref(storage, 'export.zip');
  const invoke = invocationActions(firestore, project.id);
  const strings = localizedStrings.settings.export;
  const [isExporting, setIsExporting] = useState(false);
  const [fileExists, setFileExists] = useState(false);
  const [zipurl, setZipurl] = useState('');
  const [meta, setMeta] = useState<FullMetadata>();
  const [dateString, setDateString] = useState<string>('');

  useEffect(() => {
    document.title = `${strings.title} | ${projectConfig.doc.name}`;
  }, [projectConfig.doc.name, strings.title]);

  const _export = async () => {
    toasts.info(strings.exportStarted);
    setIsExporting(true);
    try {
      await invoke.export();
      await checkRef();
      toasts.success(strings.exportFinished);
    } catch (error) {
      toasts.error(error);
    }
    setIsExporting(false);
  };

  const _downloadExport = () => {
    // set a custom file download name before downloading
    if (meta && !meta.contentDisposition) {
      void updateMetadata(fileRef, {
        contentDisposition: `attachment; filename=Export ${dateString}.zip`,
      })
        .then(() => {
          window.open(zipurl);
        })
        .catch((err) => {
          toasts.error(strings.failedToDownload);
          console.log(err);
        });
    } else {
      window.open(zipurl);
    }
  };

  const _deleteExport = async () => {
    try {
      await invoke.deleteExports(fileRef);
      toasts.success(strings.deleteFinished);
      await checkRef();
    } catch (error) {
      toasts.error(localizedStrings.error.unknown);
      console.log(error);
    }
  };

  // get download url, disable download if no export has been stored
  const checkRef = async () => {
    try {
      const url = await getDownloadURL(fileRef);
      const metadata = await getMetadata(fileRef);

      setZipurl(url);
      setMeta(metadata);
      setDateString(
        `${new Date(metadata.timeCreated).toLocaleDateString('sv')} ${new Date(metadata.timeCreated).toLocaleTimeString(
          'sv',
        )}`,
      );
      setFileExists(true);
    } catch (error) {
      setFileExists(false);
    }
  };

  useEffect(() => {
    void checkRef();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageWithSidebar>
      <SettingsSidebar />
      <main className="padded-container">
        <div className="settings-page">
          <h1>{strings.title}</h1>
          <p style={{ color: 'var(--grayscale-2)' }}>{strings.hint}</p>
          <hr />
          <IconButton
            color="primary"
            isLoading={isExporting}
            onClick={_export}
            icon={faSave}
            text={isExporting ? strings.generating : strings.create}
          />

          <div className="email-list acc-mng-module shadow-sm">
            <Row className="align-items-center text-muted">
              <span className="col-md-8 col">{!fileExists ? <em>{strings.notFound}</em> : `Export ${dateString}`}</span>
              <div className="col-md-4 col text-end">
                <IconButton
                  disabled={!fileExists}
                  color="primary"
                  onClick={_downloadExport}
                  icon={faDownload}
                  tooltip={strings.download}
                />
                <IconButton
                  disabled={!fileExists}
                  color="primary"
                  onClick={_deleteExport}
                  icon={faTrash}
                  tooltip={strings.delete}
                />
              </div>
            </Row>
          </div>
        </div>
      </main>
    </PageWithSidebar>
  );
};
